import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Ticket, Copy, Check, Link as LinkIcon, Loader2 } from "lucide-react";

export default function AdminPromosTab() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/promos/family-annual");
      setStatus(data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not load promo status.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    setCreating(true);
    setErr("");
    try {
      const { data } = await api.post("/admin/promos/family-annual");
      setStatus(data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not create the code in Stripe.");
    } finally {
      setCreating(false);
    }
  };

  const copy = async (text, which) => {
    try { await navigator.clipboard.writeText(text); } catch { /* noop */ }
    setCopied(which);
    setTimeout(() => setCopied(""), 1600);
  };

  const live = status?.exists;
  const link = status?.share_link || "";
  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }); }
    catch { return iso; }
  };

  return (
    <div data-testid="admin-promos-tab" className="max-w-3xl">
      <div className="flex items-center gap-2">
        <Ticket size={20} className="text-[var(--ff-primary)]" />
        <h2 className="font-display text-2xl text-[var(--ff-primary)]">Friends &amp; Family code</h2>
      </div>
      <p className="text-sm text-[var(--ff-text-muted)] mt-1 max-w-2xl">
        A private <strong>10% off the annual membership</strong> (first year only — renews at the normal price).
        Not shown anywhere on the site — share it by text or on a flyer. Monthly plans are not eligible.
      </p>

      {err && <div className="mt-5 card-flat p-4 text-sm text-rose-700" data-testid="promo-error">{err}</div>}

      {loading ? (
        <div className="mt-8 flex items-center gap-2 text-[var(--ff-text-muted)]"><Loader2 className="animate-spin" size={16}/> Loading…</div>
      ) : !live ? (
        <div className="mt-6 card-flat p-6" data-testid="promo-create-card">
          <p className="text-sm text-[var(--ff-text-secondary)]">
            This code hasn't been created yet. Click below to create <strong>FAMILY10</strong> in your live Stripe account
            (10% off annual, first year · max <strong>100</strong> redemptions · expires <strong>Nov 30, 2026</strong>).
          </p>
          <button
            onClick={create}
            disabled={creating}
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-[var(--ff-primary)] text-white px-5 py-2.5 text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
            data-testid="promo-create-btn"
          >
            {creating ? <><Loader2 className="animate-spin" size={15}/> Creating…</> : <>Create the FAMILY10 code</>}
          </button>
        </div>
      ) : (
        <div className="mt-6 space-y-4">
          <div className="card-flat p-6" data-testid="promo-live-card">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <p className="overline text-[var(--ff-primary)]">Live code</p>
                <p className="font-display text-4xl text-[var(--ff-primary)] mt-1" data-testid="promo-code">{status.code}</p>
              </div>
              <span className={`text-xs font-semibold px-3 py-1 rounded-full ${status.active ? "bg-emerald-100 text-emerald-700" : "bg-stone-200 text-stone-600"}`} data-testid="promo-active">
                {status.active ? "Active" : "Inactive"}
              </span>
            </div>
            <div className="mt-4 grid sm:grid-cols-3 gap-4 text-sm">
              <div><p className="text-[var(--ff-text-muted)] text-xs">Discount</p><p className="font-medium">10% off (annual, 1st yr)</p></div>
              <div><p className="text-[var(--ff-text-muted)] text-xs">Used</p><p className="font-medium" data-testid="promo-redeemed">{status.times_redeemed} / {status.max_redemptions ?? "∞"}</p></div>
              <div><p className="text-[var(--ff-text-muted)] text-xs">Expires</p><p className="font-medium">{fmtDate(status.expires_at)}</p></div>
            </div>
          </div>

          <div className="card-flat p-6" data-testid="promo-share-card">
            <p className="overline text-[var(--ff-primary)] flex items-center gap-1"><LinkIcon size={13}/> Shareable link (auto-applies the discount)</p>
            <div className="mt-3 flex flex-col sm:flex-row gap-3 sm:items-center">
              <code className="flex-1 truncate rounded-lg bg-stone-100 border border-stone-200 px-4 py-3 font-mono text-sm" data-testid="promo-link">{link}</code>
              <button onClick={() => copy(link, "link")} className="inline-flex items-center justify-center gap-1.5 text-xs border border-stone-300 hover:border-[var(--ff-primary)] rounded-full px-3 py-2 transition-colors" data-testid="promo-copy-link">
                {copied === "link" ? <><Check size={13}/> Copied</> : <><Copy size={13}/> Copy link</>}
              </button>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <button onClick={() => copy(status.code, "code")} className="inline-flex items-center gap-1.5 text-xs border border-stone-300 hover:border-[var(--ff-primary)] rounded-full px-3 py-2 transition-colors" data-testid="promo-copy-code">
                {copied === "code" ? <><Check size={13}/> Copied</> : <><Copy size={13}/> Copy code (for flyers / texts)</>}
              </button>
              <button onClick={load} className="text-xs text-[var(--ff-text-muted)] underline" data-testid="promo-refresh">Refresh usage</button>
            </div>
            <p className="mt-3 text-xs text-[var(--ff-text-muted)]">
              People can tap the link (discount applies automatically at checkout) or type <strong>{status.code}</strong> in the promo box. Works on the annual plan only.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
